import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  CodeOutlined,
  ConstructionOutlined,
  PersonOutline,
  VisibilityOutlined,
} from "@mui/icons-material";
import { grey, yellow } from "@mui/material/colors";
import { AgGridReact } from "ag-grid-react";
import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import isObject from "lodash/isObject";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AgButtonDownloadTable from "../../components/agGrid/AgButtonDownloadTable";
import AgGridTableWrapper from "../../components/agGrid/AgGridTableWrapper";
import BorrowerApplicationLinksButton from "../../components/ui/buttons/BorrowerApplicationLinksButton";

import { columnDefSuppressFloatingFilterButton } from "../../_src_shared/agGrid/columnDef/columnDefSuppressFloatingFilterButton";
import { useColumnTypes } from "../../_src_shared/utils/agGrid/columnTypes/hooks/useColumnTypes";
import CellRendererDownloadLoanApplication from "../../components/agGrid/cellRenderer/CellRendererDownloadLoanApplication";
import CellRendererLoanFiles from "../../components/agGrid/cellRenderer/CellRendererLoanFiles";
import ApplicationAssignmentModal from "../../components/common/ApplicationAssignmentModal";
import Flx from "../../components/layout/Flx";
import Screen from "../../components/layout/Screen";
import TitledHeaderWithSearch from "../../components/layout/TitledHeaderWithSearch";
import BasicModal from "../../components/modals/BasicModal";
import JsonPreviewModal from "../../components/modals/JsonPreviewModal";
import OpenInNewTabButton from "../../components/ui/buttons/OpenInNewTabButton";
import ConfirmationDialog from "../../components/ui/ConfirmationDialog";
import MoreOptionsDropdown from "../../components/ui/menus/MoreOptionsDropdown";
import { useBorrowerSubmissionsHook } from "../../hooks/useBorrowerSubmissionsHook";
import { usePipelineHook } from "../../hooks/usePipelineHook";
import { useUnderwritingHook } from "../../hooks/useUnderwritingHook";
import { cachedDataItemSet } from "../../redux/actions/cachedDataActions";
import {
  loanPipelineClear,
  loanPipelineSet,
} from "../../redux/actions/loanPipelineActions";
import { colDefFilterMultiSelect } from "../../utils/agGrid/colDefs/colDefFilterMultiSelect";
import { sideBarColumnsFilters } from "../../utils/agGrid/sideBar/sideBarColumnsFilters";
import LoanApplicationFormScreen from "../LoanApplicationFormScreen";

const modalTypesEnums = {
  PREVIEW: "preview",
  DROPDOWN: "dropdown",
  RUN_REPORT: "runReport",
  FORMSPY: "formSpy",
};

const LoanAppSubmissionsScreen = () => {
  const ref = useRef();
  const [quickFilter, setQuickFilter] = useState(null);
  const loanApps = useSelector((state) => state?.cachedData?.loanApps);
  const loanPipeline = useSelector((state) => state?.loanPipeline);

  const dispatch = useDispatch();

  const { getApplications } = useBorrowerSubmissionsHook();
  const { getUserPipeline } = usePipelineHook();

  useEffect(() => {
    if (isArray(loanApps)) {
      return;
    }

    if (!isArray(loanPipeline)) {
      getUserPipeline({
        onSuccessFn: (pipeline) => {
          dispatch(loanPipelineSet(pipeline));
          return pipeline;
        },
        onFailFn: () => {
          return [];
        },
      });
      return;
    }

    if (isNil(loanApps)) {
      getApplications({
        onSuccessFn: (d) => {
          dispatch(cachedDataItemSet("loanApps", d));
        },
        onFailFn: (err) => {
          console.error("GetUserPipeline error:", err);
        },
      });
    }
  }, [loanApps, loanPipeline]);

  return (
    <Screen>
      <TitledHeaderWithSearch
        title={"Loan Application Submissions"}
        quickFilter={quickFilter}
        setQuickFilter={setQuickFilter}
        endContent={
          <Flx g={1}>
            <BorrowerApplicationLinksButton>
              Send Loan App
            </BorrowerApplicationLinksButton>

            <AgButtonDownloadTable ref={ref} downloadName="Loan Applications" />
          </Flx>
        }
      />

      <Flx sx={{ height: "100%", position: "relative" }} g={1}>
        <AppSubmissionsTable rowData={loanApps} quickFilter={quickFilter} />
      </Flx>
    </Screen>
  );
};

const LoanAppActionsDropdown = ({ params }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { loading, createLoanFromApplication } = useUnderwritingHook();

  const applicationData = useMemo(() => params?.data, [params?.data]);

  const [assignedApplication, setAssignedApplication] = useState(null);

  const [modal, setModal] = useState(false);

  const onAssignSelected = useCallback(() => {
    setAssignedApplication(applicationData);
  }, [applicationData]);

  const clearAssignedSelect = useCallback(() => {
    setAssignedApplication(null);
  }, []);

  const onCreateLoanClick = useCallback(() => {
    const appId = params?.data?._id;

    createLoanFromApplication({
      applicationId: appId,
      onSuccessFn: (d) => {
        dispatch(cachedDataItemSet("loanApps", null));
        dispatch(loanPipelineClear());
        navigate(`/loans`);
      },
    });
  }, [params?.data?._id, createLoanFromApplication, dispatch, navigate]);

  const items = useMemo(
    () => [
      {
        label: "Create Loan From Application",
        icon: <ConstructionOutlined fontSize="small" />,
        onClick: () => setModal(modalTypesEnums.DROPDOWN),
      },
      {
        label: "Assign To Loan or Salesperson",
        icon: <PersonOutline fontSize="small" />,
        onClick: onAssignSelected,
      },
      {
        label: "Preview Application",
        icon: <VisibilityOutlined fontSize="small" />,
        onClick: () => setModal(modalTypesEnums.PREVIEW),
      },
      {
        label: "View Raw App Data",
        icon: <CodeOutlined className="thin" fontSize="small" />,

        onClick: () => setModal(modalTypesEnums.FORMSPY),
      },
    ],
    [onAssignSelected, setModal]
  );

  return (
    <>
      <MoreOptionsDropdown
        title={"Application Actions"}
        tooltip="Application Actions"
        items={items}
      />
      <JsonPreviewModal
        title="Loan Application Preview"
        values={modal === modalTypesEnums.FORMSPY ? applicationData : null}
        show={modal === modalTypesEnums.FORMSPY}
        defaultCollapse={1}
        onClose={() => setModal(false)}
      />
      <ApplicationAssignmentModal
        application={assignedApplication}
        type={"loanApps"}
        onClearSelected={clearAssignedSelect}
      />
      <ConfirmationDialog
        open={modal === modalTypesEnums.DROPDOWN}
        title="Create Loan From Application"
        message="Are you sure you want to create a loan from this application?"
        cancelText="Cancel"
        confirmText="Create Loan"
        loading={loading}
        onConfirm={() => {
          onCreateLoanClick();
          setModal(null);
        }}
        onCancel={() => setModal(false)}
      />
      <PreviewModalDialog
        show={modal === modalTypesEnums.PREVIEW}
        application={applicationData}
        onCancel={() => setModal(null)}
      />
    </>
  );
};

const PreviewModalDialog = ({ show, application, onCancel }) => {
  if (!show) {
    return null;
  }
  return (
    <BasicModal
      maxWidth={"xl"}
      title={"Application Preview"}
      show={show}
      allowFullScreen
      onClose={onCancel}
      suppressBodyPadding
    >
      <LoanApplicationFormScreen initialValues={application} />
    </BasicModal>
  );
};

const AppSubmissionsTable = ({ quickFilter, rowData }) => {
  const loanPipeline = useSelector((state) => state?.loanPipeline);
  const cellStyleUnassigned = useCallback((p) => {
    if (isNil(p.value) || p.value === "Unassigned") {
      return {
        background: yellow[50],
        color: grey[500],
      };
    }
  }, []);
  const columnDefs = useMemo(() => {
    return [
      {
        headerName: "Actions",
        pinned: "left",
        width: 90,
        resizable: false,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          paddingLeft: "6px",
        },
        suppressHeaderFilterButton: true,
        suppressFloatingFilterButton: true,
        floatingFilter: false,
        cellRenderer: (params) => {
          return (
            <Flx ac>
              <LoanAppActionsDropdown params={params} />
              <CellRendererDownloadLoanApplication {...params} />
            </Flx>
          );
        },
      },
      {
        headerName: "Assigned Loan",
        pinned: "left",
        width: 130,
        valueGetter: (p) => {
          const dealId = p?.data?.dealId;
          if (isNil(dealId)) {
            return null;
          }
          if (isNil(loanPipeline)) {
            return "Loading...";
          }
          const loanNumber = loanPipeline?.find(
            (d) => d?._id === dealId
          )?.loanNumber;
          if (isNil(loanNumber)) {
            return null;
          }
          return loanNumber;
        },
        cellRenderer: (params) => {
          const value = params?.value;
          if (isNil(value) || value === "Loading...") {
            return "Unassigned";
          }
          return (
            <OpenInNewTabButton linkIcon to={`/loan/${value}`}>
              {value}
            </OpenInNewTabButton>
          );
        },
        cellStyle: cellStyleUnassigned,
        ...columnDefSuppressFloatingFilterButton,
      },
      {
        headerName: "Assigned Salesperson",
        field: "salesperson",
        width: 260,
        pinned: "left",
        valueFormatter: (p) => {
          if (isNil(p.value)) {
            return "Unassigned";
          }
          if (isObject(p.value)) {
            return p.value?.fullName;
          }

          return p?.value;
        },

        cellStyle: cellStyleUnassigned,
        ...columnDefSuppressFloatingFilterButton,
      },
      {
        headerName: "Assigned Loan",
        field: "dealId",
        pinned: "left",
        width: 125,
        hide: true,
      },
      {
        headerName: "Assigned By",
        hide: true,
        field: "assigned_by",
        pinned: "left",
      },

      ...otherColumns,
    ];
  }, [loanPipeline, cellStyleUnassigned]);

  const defaultColDef = useMemo(() => {
    return {
      enableRowGroup: true,
      floatingFilter: true,
      filter: colDefFilterMultiSelect,
      showRowGroup: false,
      suppressHeaderFilterButton: true,
      suppressMenu: true,
    };
  }, []);

  const columnTypes = useColumnTypes();
  const sideBar = useMemo(() => sideBarColumnsFilters(), []);

  return (
    <AgGridTableWrapper height={"100%"} noBorder>
      <AgGridReact
        rowData={rowData}
        columnDefs={columnDefs}
        quickFilterText={quickFilter}
        defaultColDef={defaultColDef}
        getRowId={(params) => params.data._id}
        groupDisplayType={"groupRows"}
        columnTypes={columnTypes}
        loading={isEmpty(rowData)}
        sideBar={sideBar}
      />
    </AgGridTableWrapper>
  );
};

const otherColumns = [
  {
    headerName: "Submission Date",
    type: "date",
    field: "submission_date",
  },
  { hide: true, headerName: "_id", field: "_id" },

  {
    headerName: "Borrower Name",
    field: "fullName",

    filter: "agTextColumnFilter",
    valueGetter: (params) => {
      return `${params.data?.firstName || ""} ${params.data?.lastName || ""}`;
    },
  },
  {
    field: "documents",
    headerName: "Uploaded Files",
    cellRenderer: CellRendererLoanFiles,
    cellStyle: {
      display: "flex",
      alignItems: "center",
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      padding: "0 8px",
    },
  },
  {
    headerName: "Phone",
    field: "phone",

    filter: "agTextColumnFilter",
    valueFormatter: (params) => {
      const phone = params.value;
      if (!phone) return "";
      return phone.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
    },
  },
  {
    headerName: "Email",
    field: "emailAddress",
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Citizenship",
    field: "citizenshipStatus",
    filter: "agTextColumnFilter",
  },
  {
    headerName: "DOB",
    field: "dateOfBirth",
    filter: "agDateColumnFilter",
    type: "date",
  },
  {
    headerName: "FICO",
    field: "estimatedFICO",
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Marital Status",
    field: "maritalStatus",
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Property Address",
    field: "subjectProperties",
    filter: "agTextColumnFilter",
    valueGetter: (params) => {
      if (isEmpty(params.data?.subjectProperties)) return "None";
      return params.data?.subjectProperties[0]?.address?.fullAddress || "None";
    },
  },
  {
    headerName: "Property Type",
    field: "propertyType",
    filter: "agTextColumnFilter",
    valueGetter: (params) => {
      if (isEmpty(params.data?.subjectProperties)) return "";
      return params.data?.subjectProperties[0]?.propertyType || "";
    },
  },
  {
    headerName: "Purchase Price",
    field: "purchasePrice",
    filter: "agNumberColumnFilter",
    valueGetter: (params) => {
      if (isEmpty(params.data?.subjectProperties)) return null;
      return params.data?.subjectProperties[0]?.purchasePrice || null;
    },
    type: "dollar",
  },
  {
    headerName: "ARV",
    field: "arv",
    filter: "agNumberColumnFilter",
    valueGetter: (params) => {
      if (isEmpty(params.data?.subjectProperties)) return null;
      return params.data?.subjectProperties[0]?.arv || null;
    },
    type: "dollar",
  },
  {
    headerName: "As-Is Value",
    field: "asIsValue",
    filter: "agNumberColumnFilter",
    valueGetter: (params) => {
      if (isEmpty(params.data?.subjectProperties)) return null;
      return params.data?.subjectProperties[0]?.asIsValue || null;
    },
    type: "dollar",
  },
  {
    headerName: "Annual Taxes",
    field: "annualTaxes",
    filter: "agNumberColumnFilter",
    valueGetter: (params) => {
      if (isEmpty(params.data?.subjectProperties)) return null;
      return params.data?.subjectProperties[0]?.annualTaxes || null;
    },
    type: "dollar",
  },
  {
    headerName: "Annual Insurance",
    field: "annualInsurance",
    filter: "agNumberColumnFilter",
    valueGetter: (params) => {
      if (isEmpty(params.data?.subjectProperties)) return null;
      return params.data?.subjectProperties[0]?.annualInsurance || null;
    },
    type: "dollar",
  },
  {
    headerName: "Base Loan Amount",
    field: "baseLoanAmount",
    filter: "agNumberColumnFilter",
    type: "dollar",
  },
  {
    headerName: "Total Holdback",
    field: "totalHoldback",
    filter: "agNumberColumnFilter",
    type: "dollar",
  },
  {
    headerName: "Cash Liquidity",
    field: "cashLiquidity",
    filter: "agNumberColumnFilter",
    type: "dollar",
  },
  {
    headerName: "Other Liquidity",
    field: "otherLiquidity",
    filter: "agNumberColumnFilter",
    type: "dollar",
  },
  {
    headerName: "Loan Product Type",
    field: "loanProductType",
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Loan Purpose",
    field: "loanPurpose",
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Expected Closing",
    field: "expectedClosingDate",
    filter: "agDateColumnFilter",
    type: "date",
  },
  {
    headerName: "Loan Salesperson",
    field: "loanSalesperson",
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Property Condition",
    field: "propertyCondition",
    filter: "agTextColumnFilter",
    valueGetter: (params) => {
      if (isEmpty(params.data?.subjectProperties)) return "";
      return (
        params.data?.subjectProperties[0]?.ffPropertyStatus ||
        params.data?.subjectProperties[0]?.gucPropertyStatus ||
        ""
      );
    },
  },
  {
    headerName: "Fix & Flip Experience",
    field: "hasExperienceWithFixFlips",
    filter: "agSetColumnFilter",
    valueFormatter: (params) => (params.value ? "Yes" : "No"),
  },
  {
    headerName: "# of Fix & Flips",
    field: "fixFlipsWithinTwoYears",
    filter: "agNumberColumnFilter",
  },
  {
    headerName: "Entity Name",
    field: "entityName",
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Entity Type",
    field: "entityType",
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Entity State",
    field: "entityStateOfOrganization",
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Primary Address",
    field: "primaryAddress.fullAddress",
    filter: "agTextColumnFilter",
  },
  {
    headerName: "Flood Zone",
    field: "floodZone",
    filter: "agSetColumnFilter",
    valueGetter: (params) => {
      if (isEmpty(params.data?.subjectProperties)) return false;
      return params.data?.subjectProperties[0]?.floodZone || false;
    },
    valueFormatter: (params) => (params.value ? "Yes" : "No"),
  },
];
export default LoanAppSubmissionsScreen;
